/* Backoffice inbox — list, search, filter, open, mark read/unread, reply,
   delete, and export (single brief as text, or the whole list as CSV). */
import { useMemo, useState } from 'react'
import { useEnquiries } from './useEnquiries.js'
import { Icon } from '../components/Icon.jsx'

function dateLabel(createdAt) {
  if (!createdAt) return ''
  const d = typeof createdAt.toDate === 'function' ? createdAt.toDate() : new Date(createdAt)
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function download(filename, text, type) {
  const blob = new Blob([text], { type })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(a.href)
}

function exportBrief(e) {
  const txt = `CONFIDENTIAL BRIEF — CURATEDHOMES

Name:    ${e.name}
Email:   ${e.email}
Place:   ${e.place}
Matter:  ${e.matter}
Horizon: ${e.horizon}
Date:    ${dateLabel(e.createdAt)}

In their words:
${e.words}
`
  download(`brief-${(e.name || 'brief').replace(/\s+/g, '-').toLowerCase()}.txt`, txt, 'text/plain')
}

function fullDate(createdAt) {
  if (!createdAt) return ''
  const d = typeof createdAt.toDate === 'function' ? createdAt.toDate() : new Date(createdAt)
  return isNaN(d) ? '' : d
}

// Build a styled .xlsx workbook (ExcelJS is imported lazily so it never
// weighs down the public bundle). Bold sage header, sensible column widths,
// frozen header row, auto-filter, wrapped message column.
async function exportXlsx(list) {
  const ExcelJS = (await import('exceljs')).default
  const wb = new ExcelJS.Workbook()
  wb.creator = 'CuratedHomes'
  const ws = wb.addWorksheet('Enquiries', {
    views: [{ state: 'frozen', ySplit: 1 }],
  })

  ws.columns = [
    { header: 'Date', key: 'date', width: 14 },
    { header: 'Name', key: 'name', width: 22 },
    { header: 'Email', key: 'email', width: 28 },
    { header: 'Place', key: 'place', width: 26 },
    { header: 'Matter', key: 'matter', width: 22 },
    { header: 'Horizon', key: 'horizon', width: 18 },
    { header: 'Status', key: 'status', width: 10 },
    { header: 'In their words', key: 'words', width: 60 },
  ]

  list.forEach((e) => {
    const d = fullDate(e.createdAt)
    ws.addRow({
      date: d || '',
      name: e.name,
      email: e.email,
      place: e.place,
      matter: e.matter,
      horizon: e.horizon,
      status: e.read ? 'Read' : 'Unread',
      words: e.words,
    })
  })

  // Header styling.
  const header = ws.getRow(1)
  header.font = { bold: true, color: { argb: 'FFECE7DC' }, size: 11 }
  header.height = 22
  header.alignment = { vertical: 'middle' }
  header.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3A4A3E' } }
  })

  // Date column format + wrap the long message column.
  ws.getColumn('date').numFmt = 'yyyy-mm-dd'
  ws.getColumn('words').alignment = { wrapText: true, vertical: 'top' }
  ws.autoFilter = { from: 'A1', to: 'H1' }

  const buf = await wb.xlsx.writeBuffer()
  download(
    'enquiries.xlsx',
    buf,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  )
}

export function Enquiries() {
  const { enquiries, loading, markRead, markUnread, remove } = useEnquiries()
  const [openId, setOpenId] = useState(null)
  const [search, setSearch] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return enquiries.filter((e) => {
      if (unreadOnly && e.read) return false
      if (!q) return true
      return [e.name, e.email, e.place, e.matter, e.words]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(q))
    })
  }, [enquiries, search, unreadOnly])

  const open = filtered.find((e) => e.id === openId) || enquiries.find((e) => e.id === openId)

  if (loading) return <div className="empty">Loading…</div>
  if (!enquiries.length)
    return (
      <div className="empty">
        <Icon name="inbox" size={28} />
        <div>No briefs yet.</div>
      </div>
    )

  return (
    <div>
      <div className="inbox-toolbar">
        <div className="inbox-search">
          <Icon name="search" size={15} />
          <input
            value={search}
            placeholder="Search name, email, place, matter…"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="chips">
          <button
            className={'chip' + (!unreadOnly ? ' sel' : '')}
            onClick={() => setUnreadOnly(false)}
          >
            All
          </button>
          <button
            className={'chip' + (unreadOnly ? ' sel' : '')}
            onClick={() => setUnreadOnly(true)}
          >
            Unread
          </button>
        </div>
        <button
          className="btn-ghost"
          onClick={() => exportXlsx(filtered)}
          disabled={!filtered.length}
        >
          Export Excel
        </button>
      </div>

      <div className="inbox">
        <div className="inbox-list">
          {filtered.length === 0 ? (
            <div className="empty" style={{ padding: '40px 20px' }}>
              <div>No briefs match.</div>
            </div>
          ) : (
            filtered.map((e) => (
              <button
                key={e.id}
                className={'enq-row' + (openId === e.id ? ' active' : '')}
                onClick={() => {
                  setOpenId(e.id)
                  if (!e.read) markRead(e.id)
                }}
              >
                <span className={'dot' + (e.read ? ' read' : '')}></span>
                <div style={{ flex: 1 }}>
                  <div className="er-name">{e.name}</div>
                  <div className="er-meta">
                    {e.place} · {e.matter}
                  </div>
                </div>
                <div className="er-date">{dateLabel(e.createdAt)}</div>
              </button>
            ))
          )}
        </div>
        {open ? (
          <div className="enq-detail">
            <div className="ed-over">Confidential brief</div>
            <h2>{open.name}</h2>
            <div className="ed-row">
              <div className="ed-k">Email</div>
              <div className="ed-v">{open.email}</div>
            </div>
            <div className="ed-row">
              <div className="ed-k">The place</div>
              <div className="ed-v">{open.place}</div>
            </div>
            <div className="ed-row">
              <div className="ed-k">The matter</div>
              <div className="ed-v">{open.matter}</div>
            </div>
            <div className="ed-row">
              <div className="ed-k">The horizon</div>
              <div className="ed-v">{open.horizon}</div>
            </div>
            <div className="ed-words">&ldquo;{open.words}&rdquo;</div>
            <div className="enq-actions">
              <a
                className="btn-pub"
                href={`mailto:${open.email}?subject=${encodeURIComponent('Re: your brief — CuratedHomes')}`}
              >
                Reply
              </a>
              <button
                className="btn-ghost"
                onClick={() => (open.read ? markUnread(open.id) : markRead(open.id))}
              >
                Mark {open.read ? 'unread' : 'read'}
              </button>
              <button className="btn-ghost" onClick={() => exportBrief(open)}>
                Export
              </button>
              <button className="btn-ghost" onClick={() => remove(open.id)}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <div className="empty">
            <Icon name="inbox" size={28} />
            <div>Select a brief to read.</div>
          </div>
        )}
      </div>
    </div>
  )
}
